import {
  FC,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {Loading} from "@frontend/components"
import {Conversation} from "@frontend/routes/cases/[caseId]/components/Conversation"
import {ConversationInputField} from "@frontend/routes/cases/[caseId]/components/conversation-input-field"
import {
  OPTIMISTIC_AI_MESSAGE_ID,
  useSendMessage,
} from "@frontend/routes/cases/[caseId]/util/useSendMessage"
import {useSession} from "@frontend/session"
import {trpc} from "@frontend/utils/trpc"
import {useStabilizedValue} from "@frontend/utils/useStabilizedValue"
import {getCaseEvents, getCaseResolved} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {token} from "@styled-system/tokens"

import {getContextualStarters} from "./getContextualStarters"
import {WIDGET_HEIGHT_BREAKPOINTS, WIDGET_HEIGHT_MEDIA_QUERIES} from "./styles"
import {usePageContext} from "./usePageContext"
import {useStarterCaseCreation} from "./useStarterCaseCreation.ts"

const LAST_SEEN_MESSAGE_ID_KEY_PREFIX = "ri2-widget-lastSeenId:"

const getLastSeenMessageIdKey = (caseId: string): string =>
  `${LAST_SEEN_MESSAGE_ID_KEY_PREFIX}${caseId}`

interface Props {
  isOpen: boolean
  caseId: string | null
  onCaseCreated: (caseId: string) => void
  starterPayload?: Ri2StarterBootstrapPayload | null
  starterMessage?: string | null
  onStarterMessageConsumed?: () => void
  onUnreadMessage: (messageId: string) => void
  onLatestAssistantMessageChange: (messageId: string | null) => void
}

export const ChatPanelBody: FC<Props> = ({
  isOpen,
  caseId,
  onCaseCreated,
  starterPayload,
  starterMessage,
  onStarterMessageConsumed,
  onUnreadMessage,
  onLatestAssistantMessageChange,
}) => {
  const [initialMessage, setInitialMessage] = useState<string | null>(null)
  const [initialStarterPayload, setInitialStarterPayload] =
    useState<Ri2StarterBootstrapPayload | null>(null)

  const handleCaseCreated = (
    newCaseId: string,
    message: string | null = null,
    nextStarterPayload: Ri2StarterBootstrapPayload | null = null,
  ): void => {
    setInitialMessage(message)
    setInitialStarterPayload(nextStarterPayload)
    onCaseCreated(newCaseId)
  }

  const clearInitialMessage = (): void => {
    setInitialMessage(null)
    setInitialStarterPayload(null)
  }

  if (caseId) {
    return (
      <Suspense
        fallback={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <Loading />
          </div>
        }
      >
        <ActiveCaseView
          isOpen={isOpen}
          caseId={caseId}
          initialMessage={initialMessage}
          initialStarterPayload={initialStarterPayload}
          onInitialMessageSent={clearInitialMessage}
          starterMessage={starterMessage}
          onStarterMessageConsumed={onStarterMessageConsumed}
          onUnreadMessage={onUnreadMessage}
          onLatestAssistantMessageChange={onLatestAssistantMessageChange}
        />
      </Suspense>
    )
  }

  return (
    <WelcomeView
      onCaseCreated={handleCaseCreated}
      starterPayload={starterPayload}
      starterMessage={starterMessage}
      onStarterMessageConsumed={onStarterMessageConsumed}
    />
  )
}

// ----- Active case: renders the full Conversation component -----

interface ActiveCaseProps {
  isOpen: boolean
  caseId: string
  initialMessage: string | null
  initialStarterPayload: Ri2StarterBootstrapPayload | null
  onInitialMessageSent: () => void
  starterMessage?: string | null
  onStarterMessageConsumed?: () => void
  onUnreadMessage: (messageId: string) => void
  onLatestAssistantMessageChange: (messageId: string | null) => void
}

const ActiveCaseView: FC<ActiveCaseProps> = ({
  isOpen,
  caseId,
  initialMessage,
  initialStarterPayload,
  onInitialMessageSent,
  starterMessage,
  onStarterMessageConsumed,
  onUnreadMessage,
  onLatestAssistantMessageChange,
}) => {
  const {userRcId} = useSession()
  const utils = trpc.useUtils()
  const [casePageData] = trpc.casePageData.useSuspenseQuery({id: caseId})
  const lastHiddenAssistantMessageIdRef = useRef<string | null>(null)

  const stabilized = useStabilizedValue({casePageData})
  const {caseModel} = stabilized.casePageData

  const events = getCaseEvents(caseModel)
  const resolved = getCaseResolved(caseModel)
  const latestAssistantMessage = [...events]
    .reverse()
    .find(
      (event) =>
        event.eventType === "message" &&
        event.type === "ai" &&
        event.id !== OPTIMISTIC_AI_MESSAGE_ID,
    )
  const latestAssistantMessageId = latestAssistantMessage
    ? String(latestAssistantMessage.id)
    : null

  const {
    sendMessage,
    abortSendMessage,
    isReceivingMessage,
    receivingMessageStatus,
  } = useSendMessage(userRcId, caseModel)

  // Send the initial message once when the case is first loaded
  const hasSentInitialMessage = useRef(false)
  useEffect(() => {
    if (initialMessage && !hasSentInitialMessage.current) {
      hasSentInitialMessage.current = true
      // Small delay to ensure the component is fully mounted
      setTimeout(() => {
        sendMessage(initialMessage, undefined, initialStarterPayload)
        onInitialMessageSent()
      }, 0)
    }
  }, [initialMessage, initialStarterPayload, sendMessage, onInitialMessageSent])

  useEffect(() => {
    if (initialMessage || !initialStarterPayload || latestAssistantMessageId) {
      return
    }

    const intervalId = window.setInterval((): void => {
      utils.casePageData.invalidate({id: caseId})
    }, 400)

    return (): void => {
      window.clearInterval(intervalId)
    }
  }, [
    caseId,
    initialMessage,
    initialStarterPayload,
    latestAssistantMessageId,
    utils.casePageData,
  ])

  // Starter messages from the host (e.g. ConversationStarterRail on the
  // eCommerce site) arrive via `window.RI2Widget.expandWidget(msg)` even after
  // a case already exists. Append them as follow-up turns when the chat is
  // idle; ignore them while a previous response is still streaming
  // (`useSendMessage.sendMessage` throws if called while receiving, so we
  // can't forward the message without corrupting the in-flight response).
  useEffect(() => {
    if (!starterMessage) return
    // Let the initial-message path run first if a case was just created.
    if (initialMessage) return
    if (isReceivingMessage) {
      onStarterMessageConsumed?.()
      return
    }

    const pendingMessage = starterMessage
    onStarterMessageConsumed?.()
    // Defer by a tick so the component is fully mounted on first render.
    setTimeout(() => {
      sendMessage(pendingMessage)
    }, 0)
  }, [
    starterMessage,
    initialMessage,
    isReceivingMessage,
    sendMessage,
    onStarterMessageConsumed,
  ])

  useEffect(() => {
    onLatestAssistantMessageChange(latestAssistantMessageId)
  }, [latestAssistantMessageId, onLatestAssistantMessageChange])

  useEffect(() => {
    if (isOpen) {
      lastHiddenAssistantMessageIdRef.current = latestAssistantMessageId
      return
    }

    if (!latestAssistantMessageId) {
      return
    }

    const lastSeenMessageId = localStorage.getItem(
      getLastSeenMessageIdKey(caseId),
    )
    const hasUnreadReply = latestAssistantMessageId !== lastSeenMessageId
    const isNewHiddenReply =
      latestAssistantMessageId !== lastHiddenAssistantMessageIdRef.current

    if (hasUnreadReply && isNewHiddenReply) {
      onUnreadMessage(latestAssistantMessageId)
    }

    lastHiddenAssistantMessageIdRef.current = latestAssistantMessageId
  }, [caseId, isOpen, latestAssistantMessageId, onUnreadMessage])

  return (
    <Conversation
      caseId={caseModel.id}
      resolved={resolved}
      events={events}
      sendMessage={sendMessage}
      abortSendMessage={abortSendMessage}
      isReceivingMessage={isReceivingMessage}
      receivingMessageStatus={receivingMessageStatus}
      title={caseModel.name}
      compact
      modelNumber={caseModel.modelNumber}
    />
  )
}

// The SVG files in the icons folder use hardcoded stroke colors (e.g. stroke="#595C60"),
// so they can't respond to CSS color changes. We inline them with stroke="currentColor"
// to support the orange hover effect via the parent element's color.

const WashingMachineIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <path
      d="M9.71133 18.6311C8.65492 18.1373 7.9315 17.0809 7.9315 15.8752C7.9315 15.4963 8.01188 15.0944 8.14968 14.704"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.4981 8.29667C14.4427 5.29967 12.0198 2.99164 12.0198 2.99164C12.0198 2.99164 6.86409 7.91774 5.21058 12.7405C4.88906 13.6476 4.70534 14.5662 4.70534 15.4389C4.70534 18.2178 6.38182 20.6177 8.80468 21.7659C9.78071 22.2252 10.8716 22.4779 12.0198 22.4779C14.7298 22.4779 17.0952 21.054 18.3583 18.9412C18.7869 18.3765 19.2947 17.0755 19.2947 15.4389C19.2947 13.0566 18.5017 11.8051 18.5017 11.8051"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const RefrigeratorIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <path
      d="M4.35496 4.73474L1.83496 7.68474L4.60496 7.90474L2.08496 10.8547M21.165 14.7347H8.16504M17.165 15.7347H13.165M17.165 13.7347H13.165M8.16504 9.17947V22.7347H22.165V2.73474H8.16504V5.9842"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const OvenIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <path
      d="M18.5932 5.9495C20.9685 7.94234 22.3245 11.0544 21.9332 14.3667C21.3144 19.5717 16.5819 23.2935 11.3763 22.6656C6.17061 22.0378 2.44839 17.315 3.06724 12.11C3.68609 6.90497 8.42761 3.18319 13.6333 3.80197C14.0792 3.85657 14.5161 3.93847 14.9438 4.04766"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M12.7919 22.1585C12.7919 22.1585 11.409 20.8841 12.5207 18.3804"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M6.93507 20.2875C6.93507 20.2875 6.70007 18.4256 9.15851 17.2235"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M3.64508 15.0815C3.64508 15.0815 4.65738 13.4998 7.31467 14.1596"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M4.47656 8.98055C4.47656 8.98055 6.2752 8.42017 7.88404 10.6346"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M9.03192 4.84998C9.03192 4.84998 10.7673 5.57305 10.5775 8.30264"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M20.0497 8.37497C20.0497 8.37497 19.6339 10.2098 16.9134 10.499"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M21.3512 14.3855C21.3512 14.3855 19.8508 15.5243 17.5912 13.9968"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M18.4861 19.8356C18.4861 19.8356 16.6061 19.7452 15.856 17.115"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
  </svg>
)

const DishwasherIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M3 8H21M8 6H8.01M12 6H12.01M16 6H16.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 15H16M10 18H14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const DryerIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M3 8H21M8 6H8.01M12 6H12.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="14" r="4" stroke="currentColor" strokeWidth="2" />
    <path
      d="M10.2 12.6C11 11.9 12.4 11.9 13.2 12.6C14 13.3 14 14.6 13.2 15.3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const MicrowaveIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="3"
      y="5"
      width="18"
      height="14"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <rect
      x="6"
      y="8"
      width="9"
      height="8"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M18 9V15M20 9V15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const RangeIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="4"
      y="3"
      width="16"
      height="18"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M4 8H20M8 6H8.01M12 6H12.01M16 6H16.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="14" r="2" stroke="currentColor" strokeWidth="2" />
    <circle cx="15" cy="14" r="2" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const FreezerIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="4"
      y="3"
      width="16"
      height="18"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M4 11H20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M12 7V9M12 15V17M9.2 8.2L10.6 9.6M13.4 14.4L14.8 15.8M14.8 8.2L13.4 9.6M10.6 14.4L9.2 15.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const GarbageDisposerIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <path
      d="M5 9h14M8 9V7.5A2.5 2.5 0 0 1 10.5 5h3A2.5 2.5 0 0 1 16 7.5V9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="6.5"
      y="9"
      width="11"
      height="10"
      rx="5.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M10 13.5h4M11 16h2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const HumidifierIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="5"
      y="9"
      width="14"
      height="10"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M12 5.5c1 1.1 1.5 1.9 1.5 2.7a1.5 1.5 0 1 1-3 0c0-.8.5-1.6 1.5-2.7zM9 13.5h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AirConditionerIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="5"
      y="5"
      width="14"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 7.8v8.4M7.8 12h8.4M9.1 9.1l5.8 5.8M14.9 9.1l-5.8 5.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 19v1M12 19v1M16 19v1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const WasherDryerComboIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="4"
      y="3"
      width="16"
      height="18"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M4 8h16M8 6h.01M11 6h.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="14" r="2.6" stroke="currentColor" strokeWidth="2" />
    <circle cx="15" cy="14" r="2.6" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const FurnaceIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="5"
      y="4"
      width="14"
      height="16"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M8 9h8M9 16c0-1.8 1.5-2.6 2.5-4 .8 1 1.5 1.6 1.5 2.8a2 2 0 1 1-4 0z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HeatPumpIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 7.5v9M9 10.5l3-3 3 3M15 13.5l-3 3-3-3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AirHandlerIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="4"
      y="4"
      width="16"
      height="16"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="1.8" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 7.5c1.4 0 2.8 1.1 2.8 2.2S13.4 12 12 12M12 16.5c-1.4 0-2.8-1.1-2.8-2.2S10.6 12 12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const WaterHeaterIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <rect
      x="7"
      y="3.5"
      width="10"
      height="17"
      rx="5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M12 7.5v6.5M12 16.8v.2M10 10h4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const GenericApplianceIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
  >
    <path
      d="M8.056 12.773c-1.318 0-2.434-.457-3.35-1.372C3.792 10.486 3.334 9.37 3.334 8.053c0-1.316.458-2.432 1.373-3.347C5.622 3.791 6.738 3.334 8.056 3.334c1.317 0 2.433.457 3.348 1.372.915.915 1.373 2.031 1.373 3.347 0 .572-.101 1.126-.304 1.661a5.22 5.22 0 0 1-.748 1.38l4.868 4.865a.37.37 0 0 1 .11.237.37.37 0 0 1-.11.274.37.37 0 0 1-.525 0l-4.868-4.865a4.87 4.87 0 0 1-1.422.712 5.21 5.21 0 0 1-1.522.226zm0-.824c1.093 0 2.016-.376 2.769-1.128.752-.753 1.129-1.675 1.129-2.768 0-1.093-.377-2.015-1.129-2.767C10.072 4.534 9.15 4.158 8.056 4.158c-1.094 0-2.017.376-2.769 1.128-.753.752-1.129 1.674-1.129 2.767 0 1.093.376 2.015 1.129 2.768.752.752 1.675 1.128 2.769 1.128z"
      fill="currentColor"
    />
  </svg>
)

const getStarterIcon = (label: string): FC => {
  const lower = label.toLowerCase()

  if (
    lower.includes("garbage disposer") ||
    lower.includes("garbage disposal") ||
    lower.includes("disposer")
  ) {
    return GarbageDisposerIcon
  }

  if (lower.includes("dishwasher") || lower.includes("dish")) {
    return DishwasherIcon
  }

  if (
    lower.includes("wash") ||
    lower.includes("drain") ||
    lower.includes("laundry")
  )
    return WashingMachineIcon

  if (
    lower.includes("dryer") ||
    lower.includes("drying") ||
    lower.includes("tumble") ||
    lower.includes("lint")
  ) {
    return DryerIcon
  }

  if (
    lower.includes("microwave") ||
    lower.includes("microwaves") ||
    lower.includes("magnetron")
  ) {
    return MicrowaveIcon
  }

  if (lower.includes("freezer") || lower.includes("ice maker")) {
    return FreezerIcon
  }

  if (lower.includes("humidifier")) {
    return HumidifierIcon
  }

  if (
    lower.includes("air conditioner") ||
    lower.includes("central air") ||
    lower.includes("ac not") ||
    lower.includes("a/c")
  ) {
    return AirConditionerIcon
  }

  if (
    lower.includes("washer/dryer combo") ||
    (lower.includes("washer") &&
      lower.includes("dryer") &&
      lower.includes("combo"))
  ) {
    return WasherDryerComboIcon
  }

  if (lower.includes("furnace")) {
    return FurnaceIcon
  }

  if (lower.includes("heat pump")) {
    return HeatPumpIcon
  }

  if (lower.includes("air handler")) {
    return AirHandlerIcon
  }

  if (lower.includes("water heater")) {
    return WaterHeaterIcon
  }

  if (
    lower.includes("refrigerator") ||
    lower.includes("fridge") ||
    lower.includes("cool") ||
    lower.includes("freezer")
  )
    return RefrigeratorIcon
  if (
    lower.includes("oven") ||
    lower.includes("stove") ||
    lower.includes("range") ||
    lower.includes("cooktop") ||
    lower.includes("burner") ||
    lower.includes("heat")
  )
    return lower.includes("range") || lower.includes("cooktop")
      ? RangeIcon
      : OvenIcon
  return GenericApplianceIcon
}

const ORANGE = token("colors.brandAccent.600")
const ORANGE_HOVER_BG = token("colors.brandAccent.50")

const starterSearchInputContainerClass = css({
  background: "white",
  borderRadius: "12px",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  transition: "border-color 0.15s, background-color 0.15s",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    padding: "10px 14px",
    gap: "10px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    padding: "9px 13px",
    gap: "9px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.tight]: {
    padding: "8px 12px",
    gap: "8px",
  },
})

const starterSearchInputClass = css({
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: "16px",
  color: "#333333",
  lineHeight: 1.4,
  fontFamily: "inherit",
  padding: 0,
  margin: 0,
  height: "22px",
  boxSizing: "border-box",
  display: "block",
  "&::placeholder": {
    color: "#555555",
    opacity: 1,
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    fontSize: "15px",
    height: "20px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    fontSize: "14px",
    height: "19px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.tight]: {
    fontSize: "14px",
    height: "18px",
  },
})

/**
 * Inline input rendered in place of a starter button when the starter has
 * `kind: "search"`. On submit, the value is substituted into the starter's
 * `{query}` placeholder and handed to `onSubmit` just like any other starter.
 */
const StarterSearchInput: FC<{
  placeholder: string
  messageTemplate: string
  disabled: boolean
  onSubmit: (message: string) => void
}> = ({placeholder, messageTemplate, disabled, onSubmit}) => {
  const [value, setValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)

  const trimmed = value.trim()
  const canSubmit = !disabled && trimmed.length > 0

  const submit = (): void => {
    if (!canSubmit) return
    const message = messageTemplate.includes("{query}")
      ? messageTemplate.replace("{query}", trimmed)
      : `${messageTemplate} ${trimmed}`.trim()
    onSubmit(message)
  }

  return (
    <div
      data-widget-starter="true"
      data-widget-starter-kind="search"
      className={starterSearchInputContainerClass}
      style={{
        border: `1px solid ${focused ? ORANGE : "#00A9D7"}`,
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => inputRef.current?.focus()}
    >
      <span style={{color: "#333333", display: "flex"}}>
        <GenericApplianceIcon />
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            submit()
          }
        }}
        className={starterSearchInputClass}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          submit()
        }}
        disabled={!canSubmit}
        aria-label="Search"
        style={{
          border: "none",
          background: canSubmit ? ORANGE : "transparent",
          color: canSubmit ? "white" : "#333333",
          width: 24,
          height: 24,
          borderRadius: 999,
          cursor: canSubmit ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background-color 0.15s, color 0.15s",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 8h10m0 0L9 4m4 4l-4 4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  )
}

/** Pre-chat widget welcome input (Figma: 56px row, 10px radius, 22/32px horizontal padding). */
const welcomeConversationInputShellClass = css({
  width: "100%",
  height: "56px",
  minHeight: "56px",
  maxHeight: "56px",
  flexShrink: 0,
  alignItems: "center",
  borderRadius: "10px",
  borderWidth: "2px",
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: "22px",
  paddingRight: "32px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    height: "48px",
    minHeight: "48px",
    maxHeight: "48px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    paddingLeft: "16px",
    paddingRight: "24px",
  },
})

const scrollableContentClass = css({
  flex: 1,
  overflowY: "auto",
  overscrollBehavior: "contain",
  padding: "24px 16px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    padding: "16px 16px 12px",
    gap: "16px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    padding: "12px 16px 8px",
    gap: "12px",
  },
})

const greetingContainerClass = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  textAlign: "center",
  padding: "8px 0 4px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    gap: "8px",
    padding: "0",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    gap: "4px",
  },
})

const greetingIconClass = css({
  width: "44px",
  height: "38px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    width: "36px",
    height: "31px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    display: "none",
  },
})

const greetingTitleClass = css({
  margin: 0,
  marginBottom: "12px",
  fontSize: "17px",
  fontWeight: 700,
  color: "#1A1A1A",
  lineHeight: 1.3,
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    fontSize: "15px",
    marginBottom: "8px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    fontSize: "14px",
    marginBottom: "4px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.tight]: {
    fontSize: "13px",
    marginBottom: 0,
  },
})

const greetingSubtitleClass = css({
  margin: "6px 0 0",
  fontSize: "13px",
  color: "#333333",
  lineHeight: 1.4,
  maxWidth: "340px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    fontSize: "12px",
    lineHeight: 1.3,
    margin: "4px 0 0",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    fontSize: "11px",
    lineHeight: 1.2,
    margin: "2px 0 0",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.tight]: {
    display: "none",
  },
})

const starterButtonClass = css({
  background: "white",
  border: "1px solid #00A9D7",
  borderRadius: "12px",
  padding: "14px 16px",
  textAlign: "left",
  fontSize: "16px",
  color: "#333333",
  lineHeight: 1.4,
  transition: "border-color 0.15s, background-color 0.15s",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    padding: "10px 14px",
    fontSize: "14px",
    gap: "10px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    padding: "9px 13px",
    fontSize: "14px",
    gap: "9px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.tight]: {
    padding: "8px 12px",
    fontSize: "13px",
    gap: "8px",
  },
})

const inputAreaContainerClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: "18px 16px 16px",
  borderTop: "1px solid #E5E7EB",
  backgroundColor: "white",
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    padding: "12px 16px 12px",
    gap: "6px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    padding: "8px 16px 8px",
    gap: "4px",
  },
})

const inputAreaTitleClass = css({
  fontSize: "15px",
  fontWeight: 500,
  color: "#333333",
  lineHeight: 1.3,
  [WIDGET_HEIGHT_MEDIA_QUERIES.relaxed]: {
    fontSize: "14px",
  },
  [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
    fontSize: "13px",
  },
})

const WelcomeView: FC<{
  onCaseCreated: (caseId: string, message: string | null) => void
  starterPayload?: Ri2StarterBootstrapPayload | null
  starterMessage?: string | null
  onStarterMessageConsumed?: () => void
}> = ({
  onCaseCreated,
  starterPayload,
  starterMessage,
  onStarterMessageConsumed,
}) => {
  const {userRcId} = useSession()
  const pageContext = usePageContext()
  const [shouldHideGreetingIcon, setShouldHideGreetingIcon] = useState(
    () => window.innerHeight <= WIDGET_HEIGHT_BREAKPOINTS.compact,
  )
  const starters = useMemo(
    () => getContextualStarters(pageContext),
    [pageContext],
  )
  const [text, setText] = useState("")
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const trimmedText = text.trim()
  const {isPending, startStarterCase, startTypedCase} = useStarterCaseCreation({
    userRcId,
    pageContext: pageContext ?? undefined,
    starterPayload,
    onCaseCreated,
  })

  const onSend = (): void => {
    if (!trimmedText) return
    startTypedCase(trimmedText)
  }

  const onQuickPrompt = useCallback(
    (message: string, source: "starter" | "api" = "starter"): void => {
      startStarterCase(message, source)
    },
    [startStarterCase],
  )

  // Auto-trigger case creation when a starter message is provided, either from
  // widget starters or the host page API.
  const hasTriggeredStarter = useRef(false)
  useEffect(() => {
    if (starterMessage && !hasTriggeredStarter.current) {
      hasTriggeredStarter.current = true
      onQuickPrompt(
        starterMessage,
        window.ri2?.initialStarterPayload || window.ri2?.initialMessage
          ? "api"
          : "starter",
      )
      onStarterMessageConsumed?.()
    }
  }, [starterMessage, onQuickPrompt, onStarterMessageConsumed])

  useEffect(() => {
    const syncGreetingIconVisibility = (): void => {
      setShouldHideGreetingIcon(
        window.innerHeight <= WIDGET_HEIGHT_BREAKPOINTS.compact,
      )
    }

    syncGreetingIconVisibility()
    window.addEventListener("resize", syncGreetingIconVisibility)

    return (): void => {
      window.removeEventListener("resize", syncGreetingIconVisibility)
    }
  }, [])

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Scrollable welcome content */}
      <div className={scrollableContentClass}>
        {/* Greeting header */}
        <div className={greetingContainerClass}>
          {/* RI icon */}
          {!shouldHideGreetingIcon && (
            <svg
              className={greetingIconClass}
              viewBox="0 0 24 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 0H6L0 10.4993L6 21H18L24 10.4993L18 0Z"
                fill="#00A9D7"
              />
              <path
                d="M11.9998 2.84763L11.7521 3.54871C10.6483 6.67414 8.2138 9.13517 5.12041 10.2491L4.42651 10.4994L5.12041 10.7497C8.2138 11.8648 10.6496 14.3246 11.7521 17.45L11.9998 18.1511L12.2475 17.45C13.3513 14.3246 15.7858 11.8635 18.8792 10.7497L19.5731 10.4994L18.8792 10.2491C15.7858 9.13386 13.35 6.67414 12.2475 3.54871L11.9998 2.84763Z"
                fill="white"
              />
            </svg>
          )}
          <div>
            <p data-widget-greeting-title="true" className={greetingTitleClass}>
              {"Find the right part faster"}
            </p>
            <p
              data-widget-greeting-subtitle="true"
              className={greetingSubtitleClass}
            >
              {"Tell me the part you need"}
              <br />
              {"or the problem you’re having."}
              <br />
              {"I’ll find the exact match."}
            </p>
          </div>
        </div>

        {/* Quick-start prompts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {starters.map(({label, message, kind}) => {
            if (kind === "search") {
              return (
                <StarterSearchInput
                  key={label}
                  placeholder={label}
                  messageTemplate={message}
                  disabled={isPending}
                  onSubmit={onQuickPrompt}
                />
              )
            }
            const Icon = getStarterIcon(label)
            return (
              <button
                key={label}
                data-widget-starter="true"
                onClick={() => onQuickPrompt(message)}
                disabled={isPending}
                className={starterButtonClass}
                style={{
                  cursor: isPending ? "not-allowed" : "pointer",
                  opacity: isPending ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isPending) {
                    e.currentTarget.style.borderColor = ORANGE
                    e.currentTarget.style.backgroundColor = ORANGE_HOVER_BG
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#00A9D7"
                  e.currentTarget.style.backgroundColor = "white"
                }}
              >
                <span
                  style={{
                    color: "#333333",
                    display: "flex",
                  }}
                >
                  <Icon />
                </span>
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Input area */}
      <div className={inputAreaContainerClass}>
        <div className={inputAreaTitleClass}>{"Tell us in your own words"}</div>
        <ConversationInputField
          value={text}
          onChange={setText}
          onSend={onSend}
          onAbort={() => {}}
          disabled={isPending || !trimmedText}
          inputRef={inputRef}
          isFirstMessage
          animatedGradientBorder
          centerSideActions
          className={welcomeConversationInputShellClass}
        />
      </div>
    </div>
  )
}
